import React, { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import PremiumSidebar from './PremiumSidebar';
import { Bars3Icon, XMarkIcon, BellIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../context/AuthContext';
import { Currency } from '../utils/currencyUtils';
import { notificationsApi } from '../api/notifications';

interface PremiumLayoutProps {
  children: React.ReactNode;
  title?: string;
  subtitle?: string;
  actions?: React.ReactNode;
}

const PremiumLayout: React.FC<PremiumLayoutProps> = ({ children, title, subtitle, actions }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notificationDropdownOpen, setNotificationDropdownOpen] = useState(false);
  const notificationDropdownRef = useRef<HTMLDivElement>(null);
  const { user, updateCurrency } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const currentCurrency = (user?.preferred_currency || 'INR') as Currency;

  const { data: notifications = [] } = useQuery({
    queryKey: ['notifications', user?.id, false, 5],
    queryFn: () => notificationsApi.getAll(false, 5),
    refetchInterval: 30000,
    enabled: !!user?.id,
  });

  const { data: unreadCount = 0 } = useQuery({
    queryKey: ['notifications-unread-count', user?.id],
    queryFn: notificationsApi.getUnreadCount,
    refetchInterval: 30000,
    enabled: !!user?.id,
  });

  const markAsReadMutation = useMutation({
    mutationFn: notificationsApi.markAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
      // Force refetch header notifications
      queryClient.refetchQueries({ queryKey: ['notifications', false] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: notificationsApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
      queryClient.refetchQueries({ queryKey: ['notifications', false] });
    },
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (notificationDropdownRef.current && !notificationDropdownRef.current.contains(event.target as Node)) {
        setNotificationDropdownOpen(false);
      }
    };

    if (notificationDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [notificationDropdownOpen]);

  const handleCurrencyChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newCurrency = e.target.value as Currency;
    if (updateCurrency) {
      await updateCurrency(newCurrency);
    }
  };

  return (
    <div className="h-screen flex overflow-hidden bg-gray-50">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 flex z-40 md:hidden">
          <div 
            className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity" 
            onClick={() => setSidebarOpen(false)} 
          />
          <div className="relative flex-1 flex flex-col max-w-xs w-full bg-slate-900">
            <div className="absolute top-0 right-0 -mr-12 pt-2">
              <button
                className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white text-white hover:bg-white/10"
                onClick={() => setSidebarOpen(false)}
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <PremiumSidebar />
          </div>
        </div>
      )}

      {/* Desktop sidebar */}
      <PremiumSidebar />

      {/* Main content area */}
      <div className="flex flex-col w-0 flex-1 overflow-hidden">
        {/* Top bar - Glassmorphism */}
        <div className="relative z-20 flex-shrink-0 flex h-16 bg-white/80 backdrop-blur-md border-b border-gray-200/50 shadow-sm">
          <div className="flex-1 px-4 flex justify-between items-center">
            <div className="flex-1 flex items-center">
              <button
                className="md:hidden px-4 border-r border-gray-200 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-slate-500"
                onClick={() => setSidebarOpen(true)}
              >
                <Bars3Icon className="h-6 w-6" />
              </button>
              <div className="ml-4 md:ml-0">
                {title && (
                  <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
                )}
                {subtitle && (
                  <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>
                )}
              </div>
            </div>
            <div className="ml-4 flex items-center md:ml-6 space-x-4">
              {/* Notification Bell */}
              <div className="relative" ref={notificationDropdownRef}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    // If no notifications, navigate directly to notifications page
                    if (notifications.length === 0 && unreadCount === 0) {
                      navigate('/notifications');
                    } else {
                      // Toggle dropdown if there are notifications
                      setNotificationDropdownOpen(!notificationDropdownOpen);
                    }
                  }}
                  className="p-2 text-gray-400 hover:text-gray-500 hover:bg-gray-100/50 rounded-lg transition-colors relative cursor-pointer z-10"
                  type="button"
                  aria-label="Notifications"
                >
                  <BellIcon className="h-6 w-6" />
                  {unreadCount > 0 && (
                    <span className="absolute top-1.5 right-1.5 flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-bold text-white bg-red-500 rounded-full ring-2 ring-white">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </button>

                {/* Notification Dropdown */}
                {notificationDropdownOpen && (
                  <div className="absolute right-0 top-full mt-1 w-80 bg-white rounded-xl shadow-2xl border border-gray-200 z-[60] max-h-96 overflow-hidden flex flex-col">
                    <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                      <h3 className="text-lg font-bold text-gray-900">Notifications</h3>
                      <button
                        onClick={() => navigate('/notifications')}
                        className="text-sm text-indigo-600 hover:text-indigo-800 font-medium"
                      >
                        View All
                      </button>
                    </div>
                    <div className="overflow-y-auto flex-1">
                      {notifications.length === 0 ? (
                        <div className="p-8 text-center">
                          <BellIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                          <p className="text-sm text-gray-500 mb-4">No notifications</p>
                          <button
                            onClick={() => {
                              setNotificationDropdownOpen(false);
                              navigate('/notifications');
                            }}
                            className="text-sm text-indigo-600 hover:text-indigo-800 font-medium"
                          >
                            Go to Notifications Page
                          </button>
                        </div>
                      ) : (
                        <div className="divide-y divide-gray-100">
                          {notifications.map((notification: any) => (
                            <div
                              key={notification.id}
                              className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors ${
                                !notification.is_read ? 'bg-indigo-50/30' : ''
                              }`}
                              onClick={() => {
                                if (!notification.is_read) {
                                  markAsReadMutation.mutate(notification.id);
                                }
                                setNotificationDropdownOpen(false);
                                if (notification.entity_type === 'lead' && notification.entity_id) {
                                  navigate(`/leads/${notification.entity_id}`);
                                } else if (notification.entity_type === 'task' && notification.entity_id) {
                                  navigate('/tasks', {
                                    state: {
                                      highlightTaskId: notification.entity_id,
                                      scrollToTask: true,
                                      notificationId: notification.id,
                                    },
                                  });
                                } else if (notification.entity_type === 'account' && notification.entity_id) {
                                  navigate(`/accounts/${notification.entity_id}`);
                                } else {
                                  navigate('/notifications');
                                }
                              }}
                            >
                              <div className="flex items-start space-x-3">
                                <div className="flex-shrink-0 mt-0.5">
                                  {!notification.is_read && (
                                    <span className="block w-2 h-2 bg-indigo-600 rounded-full"></span>
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className={`text-sm font-semibold ${
                                    notification.is_read ? 'text-gray-600' : 'text-gray-900'
                                  }`}>
                                    {notification.title}
                                  </p>
                                  {notification.message && (
                                    <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                                      {notification.message}
                                    </p>
                                  )}
                                  <p className="text-xs text-gray-400 mt-1">
                                    {new Date(notification.created_at).toLocaleString()}
                                  </p>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      deleteMutation.mutate(notification.id);
                                    }}
                                    className="mt-2 inline-flex items-center text-xs font-medium text-red-600 hover:text-red-800"
                                    title="Clear notification"
                                  >
                                    <TrashIcon className="h-3.5 w-3.5 mr-1" />
                                    Clear
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    {notifications.length > 0 && (
                      <div className="p-3 border-t border-gray-200 bg-gray-50">
                        <button
                          onClick={() => {
                            navigate('/notifications');
                            setNotificationDropdownOpen(false);
                          }}
                          className="w-full text-center text-sm text-indigo-600 hover:text-indigo-800 font-medium"
                        >
                          View All Notifications
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
              {actions}
            </div>
          </div>
        </div>

        {/* Main content */}
        <main className="flex-1 relative overflow-y-auto focus:outline-none">
          <div className="py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
              {children}
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="flex-shrink-0 bg-white/80 backdrop-blur-md border-t border-gray-200/50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 py-3">
            <div className="flex items-center justify-center">
              <div className="text-sm text-gray-500">
                © {new Date().getFullYear()} Pulp Strategy CRM
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default PremiumLayout;
